import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { EditorToolbar } from '@/components/EditorToolbar';
import type { SelectionState } from '@/components/EditorToolbar';
import { ColorPicker } from '@/components/ColorPicker';
import { LinkDialog } from '@/components/LinkDialog';
import type { LinkDialogInsertData } from '@/components/LinkDialog';
import { ImageDialog } from '@/components/ImageDialog';
import type { ImageDialogInsertData } from '@/components/ImageDialog';
import { FileDialog } from '@/components/FileDialog';
import type { FileDialogInsertData } from '@/components/FileDialog';
import { TableWizard } from '@/components/TableWizard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RichTextEditorProps {
  value?: string;
  placeholder?: string;
  isReadOnly?: boolean;
  height?: string | number;
  minHeight?: string | number;
  maxHeight?: string | number | null;
  toolbarConfig?: string[] | string;
  showToolbar?: boolean;
  sanitizeOnPaste?: boolean;
  cleanLegacyFonts?: boolean;
  legacyFontPatterns?: string[];
  onImageUpload?: ((callback: (imageUrl: string, alt?: string, width?: string, height?: string, attachmentId?: number | string) => void) => void) | null;
  theme?: string;
  onChange?: (html: string) => void;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  onPaste?: (data: { original: string; sanitized: string }) => void;
  onExportPdf?: () => void;
}

export interface RichTextEditorHandle {
  getContent: () => string;
  setContent: (html: string) => void;
  insertHtml: (html: string) => void;
  focus: () => void;
  blur: () => void;
  printContent: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_TOOLBAR_CONFIG = [
  'formatting', 'fontName', 'fontSize',
  'bold', 'italic', 'underline', 'strikethrough',
  'backColor', 'foreColor', 'separator',
  'insertOrderedList', 'insertUnorderedList', 'indent', 'outdent',
  'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
  'lineHeight', 'separator',
  'createLink', 'unlink', 'insertImage', 'uploadImage', 'insertFile', 'separator',
  'insertUpperRomanList', 'insertLowerRomanList',
  'createTable',
  'addRowAbove', 'addRowBelow', 'addColumnLeft', 'addColumnRight',
  'deleteRow', 'deleteColumn',
  'mergeCellsHorizontally', 'mergeCellsVertically',
  'splitCellHorizontally', 'splitCellVertically',
  'tableAlignLeft', 'tableAlignCenter', 'tableAlignRight', 'separator',
  'undo', 'redo', 'cleanFormatting', 'copyFormat', 'applyFormat',
];

const MOBILE_TOOLBAR_CONFIG = [
  'formatting', 'fontName', 'fontSize',
  'bold', 'italic', 'underline', 'strikethrough',
  'createTable',
  'addRowAbove', 'addRowBelow', 'addColumnLeft', 'addColumnRight',
  'deleteRow', 'deleteColumn',
  'mergeCellsHorizontally', 'mergeCellsVertically',
  'splitCellHorizontally', 'splitCellVertically',
  'tableAlignLeft', 'tableAlignCenter', 'tableAlignRight',
];

const INITIAL_SELECTION_STATE: SelectionState = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  fontName: 'Arial',
  fontSize: '3',
  formatting: 'p',
  foreColor: '#000000',
  backColor: '#ffffff',
  justifyLeft: true,
  justifyCenter: false,
  justifyRight: false,
  justifyFull: false,
  orderedList: false,
  unorderedList: false,
  upperRomanList: false,
  lowerRomanList: false,
  lineHeight: '1.5',
  inTable: false,
  inTableCell: false,
  inLink: false,
};

const MAX_HISTORY_LENGTH = 100;

const FONT_SIZE_MAP: Record<string, string> = {
  '1': '8pt',
  '2': '10pt',
  '3': '12pt',
  '4': '14pt',
  '5': '18pt',
  '6': '24pt',
  '7': '36pt',
};

const FONT_SIZE_NORMALIZE_MAP: Record<string, string> = {
  '1': '10px', '2': '13px', '3': '16px', '4': '18px',
  '5': '24px', '6': '32px', '7': '48px',
};

// ---------------------------------------------------------------------------
// Helpers (pure functions, defined outside the component)
// ---------------------------------------------------------------------------

function rgbToHex(rgb: string): string | null {
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

function cleanStyleAttribute(style: CSSStyleDeclaration, allowedStyles: Set<string>): string {
  const cleanedStyles: string[] = [];
  allowedStyles.forEach(prop => {
    const value = style.getPropertyValue(prop);
    if (value && value !== 'initial' && value !== 'inherit') {
      let cleanValue = value;
      if (prop.indexOf('color') !== -1 && value.indexOf('rgb') === 0) {
        cleanValue = rgbToHex(value) || value;
      }
      if (prop === 'font-weight' && (value === '400' || value === 'normal')) return;
      if (prop === 'font-style' && value === 'normal') return;
      if (prop === 'text-decoration' && value === 'none') return;
      if (prop === 'text-align' && value === 'start') return;
      cleanedStyles.push(`${prop}: ${cleanValue}`);
    }
  });
  return cleanedStyles.join('; ');
}

function isFontAvailable(fontName: string): boolean {
  const genericFonts = ['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui'];
  if (genericFonts.indexOf(fontName.toLowerCase()) !== -1) {
    return false;
  }
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return false;
  context.font = `${testSize} monospace`;
  const fallbackWidth = context.measureText(testString).width;
  context.font = `${testSize} "${fontName}", monospace`;
  const testWidth = context.measureText(testString).width;
  return testWidth !== fallbackWidth;
}

function normalizeTableWidths(container: HTMLElement): void {
  const tables = container.querySelectorAll('table');
  tables.forEach((table: HTMLTableElement) => {
    table.style.width = '100%';
    table.removeAttribute('width');

    const rows = table.querySelectorAll('tr');
    if (rows.length === 0) return;

    let firstRowCells: NodeListOf<HTMLTableCellElement> | null = null;
    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].querySelectorAll('td, th');
      if (cells.length > 0) {
        firstRowCells = cells as NodeListOf<HTMLTableCellElement>;
        break;
      }
    }

    if (!firstRowCells || firstRowCells.length === 0) return;

    const columnWidths: number[] = [];
    let totalWidth = 0;
    let hasWidths = false;

    firstRowCells.forEach((cell: HTMLTableCellElement) => {
      const colspan = cell.colSpan || 1;
      let width = 0;
      const styleWidth = cell.style.width;
      const attrWidth = cell.getAttribute('width');

      if (styleWidth) {
        if (styleWidth.indexOf('px') !== -1) {
          width = parseFloat(styleWidth);
          hasWidths = true;
        } else if (styleWidth.indexOf('%') !== -1) {
          width = parseFloat(styleWidth);
          hasWidths = true;
        }
      } else if (attrWidth) {
        width = parseFloat(attrWidth);
        hasWidths = true;
      }

      const widthPerCol = width / colspan;
      for (let i = 0; i < colspan; i++) {
        columnWidths.push(widthPerCol);
        totalWidth += widthPerCol;
      }
    });

    const colCount = columnWidths.length;
    let percentages: number[];

    if (hasWidths && totalWidth > 0) {
      percentages = columnWidths.map(w => (w / totalWidth) * 100);
    } else {
      const equalPercent = 100 / colCount;
      percentages = columnWidths.map(() => equalPercent);
    }

    rows.forEach((row: Element) => {
      const cells = row.querySelectorAll('td, th');
      let colIndex = 0;
      cells.forEach((cell: Element) => {
        const td = cell as HTMLTableCellElement;
        const colspan = td.colSpan || 1;
        let cellPercent = 0;
        for (let i = 0; i < colspan && colIndex + i < percentages.length; i++) {
          cellPercent += percentages[colIndex + i];
        }
        td.style.width = cellPercent.toFixed(2) + '%';
        td.removeAttribute('width');
        colIndex += colspan;
      });
    });

    const cols = table.querySelectorAll('colgroup, col');
    cols.forEach((col: Element) => {
      (col as HTMLElement).style.width = '';
      col.removeAttribute('width');
    });
  });
}

function normalizeFontTags(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const fontTags = temp.querySelectorAll('font[color], font[size], font[face]');
  fontTags.forEach((el: Element) => {
    const font = el as HTMLElement;
    const color = font.getAttribute('color');
    const size = font.getAttribute('size');
    const face = font.getAttribute('face');
    const styles: string[] = [];
    if (color) {
      styles.push(`color: ${color}`);
      font.removeAttribute('color');
    }
    if (size) {
      const cssSize = FONT_SIZE_NORMALIZE_MAP[size] || size;
      styles.push(`font-size: ${cssSize}`);
      font.removeAttribute('size');
    }
    if (face) {
      styles.push(`font-family: ${face}`);
      font.removeAttribute('face');
    }
    if (styles.length > 0) {
      const existingStyle = font.getAttribute('style') || '';
      const newStyle = existingStyle
        ? `${existingStyle}; ${styles.join('; ')}`
        : styles.join('; ');
      font.setAttribute('style', newStyle);
    }
  });
  return temp.innerHTML;
}

function convertWordLists(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  const listParagraphs = temp.querySelectorAll(
    'p.MsoListParagraphCxSpFirst, p.MsoListParagraphCxSpMiddle, p.MsoListParagraphCxSpLast, p.MsoListParagraph, p[style*="mso-list"]'
  );

  if (listParagraphs.length === 0) {
    const allParagraphs = temp.querySelectorAll('p');
    const listGroups: HTMLParagraphElement[][] = [];
    let currentGroup: HTMLParagraphElement[] = [];

    allParagraphs.forEach((p) => {
      const text = p.textContent?.trim() || '';
      const isNumbered = /^\d+[\.\)]\s/.test(text);
      const isBullet = /^[\u2022\-\*\u25CB\u25CF\u25E6\u25AA\u25AB]\s/.test(text);
      if (isNumbered || isBullet) {
        currentGroup.push(p as HTMLParagraphElement);
      } else if (currentGroup.length > 0) {
        listGroups.push(currentGroup);
        currentGroup = [];
      }
    });

    if (currentGroup.length > 0) {
      listGroups.push(currentGroup);
    }

    listGroups.forEach(group => {
      if (group.length === 0) return;
      const firstText = group[0].textContent?.trim() || '';
      const isOrdered = /^\d+[\.\)]\s/.test(firstText);
      const list = document.createElement(isOrdered ? 'ol' : 'ul');
      group.forEach(p => {
        const li = document.createElement('li');
        let content = p.innerHTML;
        content = content.replace(
          /^[\s]*(\d+[\.\)]|[\u2022\-\*\u25CB\u25CF\u25E6\u25AA\u25AB])[\s]+/,
          ''
        );
        li.innerHTML = content;
        list.appendChild(li);
      });
      group[0].parentNode?.insertBefore(list, group[0]);
      group.forEach(p => p.remove());
    });

    return temp.innerHTML;
  }

  // Process Word-style list paragraphs
  let currentList: HTMLOListElement | HTMLUListElement | null = null;

  listParagraphs.forEach((p) => {
    const paragraph = p as HTMLParagraphElement;
    const className = paragraph.className;
    const text = paragraph.textContent?.trim() || '';
    const isOrdered = /^\d+[\.\)]/.test(text) || /^[ivxIVX]+[\.\)]/.test(text) || /^[a-zA-Z][\.\)]/.test(text);
    const isFirst = className.indexOf('First') !== -1 || !currentList;
    const isLast = className.indexOf('Last') !== -1;

    if (isFirst || !currentList) {
      currentList = document.createElement(isOrdered ? 'ol' : 'ul');
      paragraph.parentNode?.insertBefore(currentList, paragraph);
    }

    const li = document.createElement('li');
    const clone = paragraph.cloneNode(true) as HTMLElement;
    clone.innerHTML = clone.innerHTML
      .replace(/<!--\[if !supportLists\]-->[\s\S]*?<!--\[endif\]-->/gi, '')
      .replace(/<!--\[if !supportLists[\s\S]*?<!\[endif\]-->/gi, '');

    const ignoreSpans = clone.querySelectorAll(
      'span[style*="mso-list:Ignore"], span[style*="mso-list: Ignore"]'
    );
    ignoreSpans.forEach(span => span.remove());

    const dirSpans = clone.querySelectorAll('span[dir]');
    dirSpans.forEach(span => {
      if (!span.textContent?.trim()) {
        span.remove();
      }
    });

    let content = clone.innerHTML.trim();
    content = content.replace(
      /^[\s]*(\d+[\.\)]|[a-zA-Z][\.\)]|[ivxIVX]+[\.\)]|[\u2022\-\*\u25CB\u25CF\u25E6\u25AA\u25AB])[\s]*/,
      ''
    );

    li.innerHTML = content || paragraph.textContent?.replace(/^[\s]*\d+[\.\)]\s*/, '').trim() || '';
    currentList!.appendChild(li);
    paragraph.remove();

    if (isLast) {
      currentList = null;
    }
  });

  return temp.innerHTML;
}

function sanitizeHtml(html: string): string {
  // First, convert Word-style lists to proper HTML lists
  let html_converted = convertWordLists(html);

  // Remove Word/Office specific markup before parsing
  let cleaned = html_converted
    .replace(/<!--\[if[^>]*>[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<o:p[\s\S]*?<\/o:p>/gi, '')
    .replace(/<w:[^>]*>[\s\S]*?<\/w:[^>]*>/gi, '')
    .replace(/<m:[^>]*>[\s\S]*?<\/m:[^>]*>/gi, '')
    .replace(/<\/?[a-z]+:[^>]*>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<span><\/span>/gi, '')
    .replace(/<b[^>]*id="docs-internal[^"]*"[^>]*>/gi, '<b>')
    .replace(/<br class="Apple-interchange-newline">/gi, '<br>');

  const temp = document.createElement('div');
  temp.innerHTML = cleaned;

  const scripts = temp.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  const styles = temp.querySelectorAll('style');
  styles.forEach(style => style.remove());
  const titles = temp.querySelectorAll('title');
  titles.forEach(title => title.remove());

  const allowedTags = new Set([
    'P', 'DIV', 'BR', 'HR',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'DEL', 'INS',
    'SUB', 'SUP', 'MARK',
    'UL', 'OL', 'LI',
    'A', 'IMG',
    'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION', 'COLGROUP', 'COL',
    'BLOCKQUOTE', 'PRE', 'CODE',
    'SPAN', 'FONT',
  ]);

  const allowedAttrs: Record<string, Set<string>> = {
    'A': new Set(['href', 'title', 'target']),
    'IMG': new Set(['src', 'alt', 'title', 'width', 'height']),
    'TD': new Set(['colspan', 'rowspan', 'width', 'height']),
    'TH': new Set(['colspan', 'rowspan', 'scope', 'width', 'height']),
    'TR': new Set(['height']),
    'COL': new Set(['span', 'width']),
    'COLGROUP': new Set(['span']),
    'OL': new Set(['type', 'start']),
    'TABLE': new Set(['border', 'width', 'cellpadding', 'cellspacing']),
    'FONT': new Set(['color', 'size']),
  };

  const allowedStyles = new Set([
    'color', 'background-color', 'background',
    'font-weight', 'font-style', 'text-decoration',
    'text-align', 'vertical-align',
    'font-size', 'font-family',
    'list-style-type', 'line-height',
    'width', 'height', 'min-width', 'min-height',
    'border', 'border-width', 'border-style', 'border-color',
    'border-top', 'border-right', 'border-bottom', 'border-left',
    'border-collapse', 'border-spacing',
    'padding',
  ]);

  const processNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tagName = el.tagName.toUpperCase();

      if (!allowedTags.has(tagName)) {
        const parent = el.parentNode;
        if (parent) {
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
        }
        return;
      }

      const tagAllowedAttrs = allowedAttrs[tagName] || new Set();
      const attrs = Array.from(el.attributes);
      attrs.forEach(attr => {
        const name = attr.name.toLowerCase();
        const value = attr.value.toLowerCase();

        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
          return;
        }
        if (value.indexOf('javascript:') !== -1) {
          el.removeAttribute(attr.name);
          return;
        }
        if (name === 'class' || name === 'id') {
          el.removeAttribute(attr.name);
          return;
        }
        if (name === 'style') {
          const cleanedStyle = cleanStyleAttribute(el.style, allowedStyles);
          if (cleanedStyle) {
            el.setAttribute('style', cleanedStyle);
          } else {
            el.removeAttribute('style');
          }
          return;
        }
        if (!tagAllowedAttrs.has(name)) {
          el.removeAttribute(attr.name);
        }
      });
    }

    const children = Array.from(node.childNodes);
    children.forEach(child => processNode(child));
  };

  processNode(temp);

  const emptyCells = temp.querySelectorAll('td, th');
  emptyCells.forEach((cell) => {
    const textContent = cell.textContent || '';
    const hasOnlyWhitespace = !textContent.trim();
    if (hasOnlyWhitespace) {
      cell.innerHTML = '&nbsp;';
      if (!(cell as HTMLElement).style.height) {
        (cell as HTMLElement).style.minHeight = '1em';
      }
    }
  });

  normalizeTableWidths(temp);

  let result = temp.innerHTML
    .replace(/<(p|div)>\s*<\/\1>/gi, '')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .replace(/>\s+</g, '> <')
    .replace(/<(br|hr|img)([^>]*[^\/])>/gi, '<$1$2 />');

  return result.trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value = '',
      placeholder = '',
      isReadOnly = false,
      height = '300px',
      minHeight = '150px',
      maxHeight = null,
      toolbarConfig = 'default',
      showToolbar = true,
      sanitizeOnPaste = true,
      cleanLegacyFonts = true,
      legacyFontPatterns = ['poppins', 'kendo', 'telerik'],
      onImageUpload = null,
      theme: themeProp,
      onChange,
      onFocus,
      onBlur,
      onPaste,
      onExportPdf,
    },
    ref
  ) {
    const { theme: globalTheme } = useTheme();

    // -----------------------------------------------------------------------
    // Refs (mutable state that should NOT trigger re-renders)
    // -----------------------------------------------------------------------
    const editorContentRef = useRef<HTMLDivElement>(null);
    const historyRef = useRef<string[]>([]);
    const historyIndexRef = useRef(-1);
    const historyDebounceTimerRef = useRef<number | null>(null);
    const selectionDebounceTimerRef = useRef<number | null>(null);
    const pendingFormatRef = useRef<Record<string, string> | null>(null);
    const copiedFormatRef = useRef<Record<string, string> | null>(null);
    const savedSelectionRef = useRef<Range | null>(null);
    const editorContentStrRef = useRef('');
    // Track whether we've done the initial mount setup
    const mountedRef = useRef(false);
    // Track previous value prop to avoid unnecessary updates
    const prevValueRef = useRef(value);

    // -----------------------------------------------------------------------
    // State (triggers re-renders)
    // -----------------------------------------------------------------------
    const [isFocused, setIsFocused] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [selectionState, setSelectionState] = useState<SelectionState>({ ...INITIAL_SELECTION_STATE });

    // Dialog state
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkDialogUrl, setLinkDialogUrl] = useState('');
    const [linkDialogText, setLinkDialogText] = useState('');
    const [linkDialogTarget, setLinkDialogTarget] = useState('_self');
    const [linkDialogIsEdit, setLinkDialogIsEdit] = useState(false);

    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [imageDialogSrc, setImageDialogSrc] = useState('');
    const [imageDialogAlt, setImageDialogAlt] = useState('');
    const [imageDialogWidth, setImageDialogWidth] = useState('');
    const [imageDialogHeight, setImageDialogHeight] = useState('');

    const [fileDialogOpen, setFileDialogOpen] = useState(false);
    const [fileDialogUrl, setFileDialogUrl] = useState('');
    const [fileDialogText, setFileDialogText] = useState('');

    const [tableWizardOpen, setTableWizardOpen] = useState(false);

    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [colorPickerType, setColorPickerType] = useState<'fore' | 'back'>('fore');
    const [colorPickerCurrentColor, setColorPickerCurrentColor] = useState('');

    // -----------------------------------------------------------------------
    // Computed values
    // -----------------------------------------------------------------------
    const activeTheme = useMemo(() => {
      return themeProp || globalTheme;
    }, [themeProp, globalTheme]);

    const isEmpty = useMemo(() => {
      const content = editorContentStrRef.current;
      if (!content) return true;
      const stripped = content.replace(/<[^>]*>/g, '').trim();
      return stripped.length === 0;
    }, [/* re-evaluated via selectionState changes which trigger renders */]);

    const canUndo = historyIndexRef.current > 0;
    const canRedo = historyIndexRef.current < historyRef.current.length - 1;

    const editorClasses = useMemo(() => {
      const classes: string[] = ['kst-rich-text-editor'];
      if (isFocused) classes.push('kst-rich-text-editor--focused');
      if (isReadOnly) classes.push('kst-rich-text-editor--readonly');
      if (isMobile) classes.push('kst-rich-text-editor--mobile');
      if (activeTheme) classes.push(activeTheme);
      return classes.join(' ');
    }, [isFocused, isReadOnly, isMobile, activeTheme]);

    const resolvedToolbarConfig = useMemo((): string[] => {
      if (Array.isArray(toolbarConfig)) {
        return toolbarConfig;
      }
      if (toolbarConfig === 'mobile' || isMobile) {
        return MOBILE_TOOLBAR_CONFIG;
      }
      return DEFAULT_TOOLBAR_CONFIG;
    }, [toolbarConfig, isMobile]);

    // -----------------------------------------------------------------------
    // stripLegacyFonts
    // -----------------------------------------------------------------------
    const stripLegacyFonts = useCallback((html: string): string => {
      let processed = normalizeFontTags(html);
      if (!cleanLegacyFonts || !legacyFontPatterns || legacyFontPatterns.length === 0) {
        return processed;
      }
      const temp = document.createElement('div');
      temp.innerHTML = processed;
      const patternRegex = new RegExp(legacyFontPatterns.join('|'), 'i');
      const elementsWithStyle = temp.querySelectorAll('[style]');
      elementsWithStyle.forEach((el: Element) => {
        const element = el as HTMLElement;
        const fontFamily = element.style.fontFamily;
        if (fontFamily && patternRegex.test(fontFamily)) {
          element.style.removeProperty('font-family');
          if (!element.getAttribute('style')?.trim()) {
            element.removeAttribute('style');
          }
        }
      });
      return temp.innerHTML;
    }, [cleanLegacyFonts, legacyFontPatterns]);

    // -----------------------------------------------------------------------
    // Selection helpers
    // -----------------------------------------------------------------------
    const saveSelection = useCallback((): Range | null => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        return selection.getRangeAt(0).cloneRange();
      }
      return null;
    }, []);

    const restoreSelection = useCallback((range: Range): void => {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, []);

    const getSelectedHtml = useCallback((): string => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const contents = range.cloneContents();
        const div = document.createElement('div');
        div.appendChild(contents);
        return div.innerHTML;
      }
      return '';
    }, []);

    const findAncestor = useCallback((node: Node | null, tagName: string): HTMLElement | null => {
      const upperTagName = tagName.toUpperCase();
      while (node) {
        if (node === editorContentRef.current) {
          return null;
        }
        if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === upperTagName) {
          return node as HTMLElement;
        }
        node = node.parentNode;
      }
      return null;
    }, []);

    // -----------------------------------------------------------------------
    // Content helpers
    // -----------------------------------------------------------------------
    const getContent = useCallback((): string => {
      if (editorContentRef.current) {
        return editorContentRef.current.innerHTML;
      }
      return '';
    }, []);

    // We use a separate ref-based forceUpdate trigger for canUndo/canRedo
    // since they derive from refs. We piggyback on selectionState updates.
    const [, forceRender] = useState(0);

    const pushToHistory = useCallback((): void => {
      const content = getContent();
      if (content === historyRef.current[historyIndexRef.current]) {
        return;
      }
      if (historyIndexRef.current < historyRef.current.length - 1) {
        historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
      }
      historyRef.current.push(content);
      if (historyRef.current.length > MAX_HISTORY_LENGTH) {
        historyRef.current.shift();
      }
      historyIndexRef.current = historyRef.current.length - 1;
      editorContentStrRef.current = content;
      onChange?.(content);
      forceRender(c => c + 1);
    }, [getContent, onChange]);

    const insertHtml = useCallback((html: string): void => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editorContentRef.current && editorContentRef.current.contains(range.commonAncestorContainer)) {
          range.deleteContents();
          const fragment = document.createRange().createContextualFragment(html);
          range.insertNode(fragment);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          editorContentStrRef.current = editorContentRef.current.innerHTML;
          pushToHistory();
        }
      }
    }, [pushToHistory]);

    const setContentInternal = useCallback((html: string): void => {
      if (editorContentRef.current) {
        const cleanedHtml = stripLegacyFonts(html);
        editorContentRef.current.innerHTML = cleanedHtml;
        editorContentStrRef.current = cleanedHtml;
        pushToHistory();
      }
    }, [stripLegacyFonts, pushToHistory]);

    const focusEditor = useCallback((): void => {
      if (editorContentRef.current) {
        editorContentRef.current.focus();
      }
    }, []);

    const blurEditor = useCallback((): void => {
      if (editorContentRef.current) {
        editorContentRef.current.blur();
      }
    }, []);

    // -----------------------------------------------------------------------
    // Selection state updating
    // -----------------------------------------------------------------------
    const getCurrentListElement = useCallback((): HTMLOListElement | HTMLUListElement | null => {
      let anchorNode: Node | null = null;
      if (savedSelectionRef.current) {
        anchorNode = savedSelectionRef.current.startContainer;
      } else {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          anchorNode = selection.anchorNode;
        }
      }
      if (!anchorNode) return null;
      if (!editorContentRef.current || !editorContentRef.current.contains(anchorNode)) return null;

      const ol = findAncestor(anchorNode, 'OL') as HTMLOListElement | null;
      if (ol) return ol;
      const ul = findAncestor(anchorNode, 'UL') as HTMLUListElement | null;
      if (ul) return ul;
      return null;
    }, [findAncestor]);

    const updateSelectionState = useCallback((): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const anchorNode = selection.anchorNode;
      if (!anchorNode) return;
      if (!editorContentRef.current || !editorContentRef.current.contains(anchorNode)) return;

      // Find the parent element of the anchor node
      let parentElement: Element | null = null;
      if (anchorNode.nodeType === Node.TEXT_NODE) {
        parentElement = anchorNode.parentElement;
      } else if (anchorNode.nodeType === Node.ELEMENT_NODE) {
        parentElement = anchorNode as Element;
      }

      const newState: SelectionState = { ...INITIAL_SELECTION_STATE };

      newState.bold = document.queryCommandState('bold');
      newState.italic = document.queryCommandState('italic');
      newState.underline = document.queryCommandState('underline');
      newState.strikethrough = document.queryCommandState('strikethrough');

      // Get font info from computed style
      let styleNode: Node | null = anchorNode;
      if (styleNode && styleNode.nodeType === Node.TEXT_NODE) {
        styleNode = styleNode.parentNode;
      }

      let fontName = 'Arial';
      let fontSize = '3';

      if (styleNode && styleNode.nodeType === Node.ELEMENT_NODE) {
        const elemStyle = window.getComputedStyle(styleNode as Element);
        let computedFont = elemStyle.fontFamily || 'Arial';
        if (computedFont.indexOf(',') !== -1) {
          const fonts = computedFont.split(',').map((f: string) => f.trim().replace(/["']/g, ''));
          fontName = fonts[0];
          for (const font of fonts) {
            if (isFontAvailable(font)) {
              fontName = font;
              break;
            }
          }
        } else {
          fontName = computedFont.replace(/["']/g, '');
        }

        const computedSize = parseFloat(elemStyle.fontSize);
        if (!isNaN(computedSize)) {
          const ptSize = computedSize * 0.75;
          if (ptSize <= 9) fontSize = '1';
          else if (ptSize <= 11) fontSize = '2';
          else if (ptSize <= 13) fontSize = '3';
          else if (ptSize <= 16) fontSize = '4';
          else if (ptSize <= 21) fontSize = '5';
          else if (ptSize <= 30) fontSize = '6';
          else fontSize = '7';
        }
      }

      newState.fontName = fontName;
      newState.fontSize = fontSize;

      // Check alignment
      const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE'];
      let blockParent: HTMLElement | null = null;
      let node: Node | null = anchorNode;

      while (node && node !== editorContentRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && blockTags.indexOf((node as Element).tagName) !== -1) {
          blockParent = node as HTMLElement;
          break;
        }
        node = node.parentNode;
      }

      if (!blockParent) {
        blockParent = editorContentRef.current;
      }

      if (blockParent) {
        const computedStyle = window.getComputedStyle(blockParent);
        const textAlign = computedStyle.textAlign;
        newState.justifyLeft = textAlign === 'left' || textAlign === 'start';
        newState.justifyCenter = textAlign === 'center';
        newState.justifyRight = textAlign === 'right';
        newState.justifyFull = textAlign === 'justify';

        // Check line height
        const computedLineHeight = computedStyle.lineHeight;
        const computedFontSize = parseFloat(computedStyle.fontSize);
        if (computedLineHeight && computedLineHeight !== 'normal' && computedFontSize) {
          const lineHeightPx = parseFloat(computedLineHeight);
          if (!isNaN(lineHeightPx) && !isNaN(computedFontSize)) {
            const ratio = lineHeightPx / computedFontSize;
            const commonValues = [1, 1.15, 1.5, 2, 2.5, 3];
            let closest = commonValues[0];
            let closestDiff = Math.abs(ratio - closest);
            for (const val of commonValues) {
              const diff = Math.abs(ratio - val);
              if (diff < closestDiff) {
                closest = val;
                closestDiff = diff;
              }
            }
            newState.lineHeight = String(closest);
          } else {
            newState.lineHeight = '1.5';
          }
        } else {
          newState.lineHeight = '1.5';
        }
      }

      // Determine block format
      const formatBlock = document.queryCommandValue('formatBlock');
      newState.formatting = formatBlock || 'p';

      // Check list state
      const isOrderedList = document.queryCommandState('insertOrderedList');
      newState.orderedList = isOrderedList;
      newState.unorderedList = document.queryCommandState('insertUnorderedList');

      newState.upperRomanList = false;
      newState.lowerRomanList = false;
      if (isOrderedList) {
        // Need to find the OL element manually since getCurrentListElement
        // uses savedSelectionRef which may not be set here
        let listAnchorNode: Node | null = anchorNode;
        if (!listAnchorNode) { /* no-op */ }
        else if (editorContentRef.current && editorContentRef.current.contains(listAnchorNode)) {
          const ol = findAncestor(listAnchorNode, 'OL') as HTMLOListElement | null;
          if (ol && ol.tagName === 'OL') {
            const listType = ol.getAttribute('type') || ol.style.listStyleType || '';
            if (listType === 'I' || listType === 'upper-roman') {
              newState.upperRomanList = true;
            } else if (listType === 'i' || listType === 'lower-roman') {
              newState.lowerRomanList = true;
            }
          }
        }
      }

      // Check table/link context
      newState.inTable = !!findAncestor(anchorNode, 'TABLE');
      newState.inTableCell = !!findAncestor(anchorNode, 'TD') || !!findAncestor(anchorNode, 'TH');
      newState.inLink = !!findAncestor(anchorNode, 'A');

      setSelectionState(newState);
    }, [findAncestor]);

    // -----------------------------------------------------------------------
    // Formatting commands
    // -----------------------------------------------------------------------
    const execCommand = useCallback((command: string, cmdValue?: string | null): void => {
      const savedRange = savedSelectionRef.current || saveSelection();
      savedSelectionRef.current = null;

      if (savedRange) {
        restoreSelection(savedRange);
      }
      focusEditor();

      document.execCommand(command, false, cmdValue || undefined);

      pushToHistory();
      updateSelectionState();
    }, [saveSelection, restoreSelection, focusEditor, pushToHistory, updateSelectionState]);

    const toggleBold = useCallback(() => execCommand('bold'), [execCommand]);
    const toggleItalic = useCallback(() => execCommand('italic'), [execCommand]);
    const toggleUnderline = useCallback(() => execCommand('underline'), [execCommand]);
    const toggleStrikethrough = useCallback(() => execCommand('strikethrough'), [execCommand]);

    const applyPendingFormat = useCallback((): void => {
      if (!pendingFormatRef.current) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) {
        return;
      }
      const span = document.createElement('span');
      if (pendingFormatRef.current.fontFamily) {
        span.style.fontFamily = pendingFormatRef.current.fontFamily;
      }
      if (pendingFormatRef.current.fontSize) {
        span.style.fontSize = pendingFormatRef.current.fontSize;
      }
      if (pendingFormatRef.current.color) {
        span.style.color = pendingFormatRef.current.color;
      }
      span.appendChild(document.createTextNode('\u200B'));
      range.insertNode(span);
      range.setStartAfter(span.firstChild as Node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      pendingFormatRef.current = null;
    }, []);

    const setFontName = useCallback((fontNameVal: string): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        pendingFormatRef.current = pendingFormatRef.current || {};
        pendingFormatRef.current.fontFamily = fontNameVal;
        setSelectionState(prev => ({ ...prev, fontName: fontNameVal }));
        return;
      }
      const range = selection.getRangeAt(0);
      if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) {
        return;
      }
      if (range.collapsed) {
        pendingFormatRef.current = pendingFormatRef.current || {};
        pendingFormatRef.current.fontFamily = fontNameVal;
        setSelectionState(prev => ({ ...prev, fontName: fontNameVal }));
        return;
      }
      const span = document.createElement('span');
      span.style.fontFamily = fontNameVal;
      try {
        range.surroundContents(span);
      } catch (e) {
        const fragment = range.extractContents();
        span.appendChild(fragment);
        range.insertNode(span);
      }
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
      pushToHistory();
      updateSelectionState();
    }, [pushToHistory, updateSelectionState]);

    const setFontSize = useCallback((fontSizeVal: string): void => {
      const ptSize = FONT_SIZE_MAP[fontSizeVal] || fontSizeVal;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        pendingFormatRef.current = pendingFormatRef.current || {};
        pendingFormatRef.current.fontSize = ptSize;
        setSelectionState(prev => ({ ...prev, fontSize: fontSizeVal }));
        return;
      }
      const range = selection.getRangeAt(0);
      if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) {
        return;
      }
      if (range.collapsed) {
        pendingFormatRef.current = pendingFormatRef.current || {};
        pendingFormatRef.current.fontSize = ptSize;
        setSelectionState(prev => ({ ...prev, fontSize: fontSizeVal }));
        return;
      }
      const span = document.createElement('span');
      span.style.fontSize = ptSize;
      try {
        range.surroundContents(span);
      } catch (e) {
        const fragment = range.extractContents();
        span.appendChild(fragment);
        range.insertNode(span);
      }
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
      pushToHistory();
      updateSelectionState();
    }, [pushToHistory, updateSelectionState]);

    const setFormatting = useCallback((tag: string): void => {
      const formattedTag = tag.startsWith('<') ? tag : `<${tag}>`;
      execCommand('formatBlock', formattedTag);
    }, [execCommand]);

    const setForeColor = useCallback((color: string): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        pendingFormatRef.current = pendingFormatRef.current || {};
        pendingFormatRef.current.color = color;
        setSelectionState(prev => ({ ...prev, foreColor: color }));
        return;
      }
      const range = selection.getRangeAt(0);
      if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) {
        return;
      }
      if (range.collapsed) {
        pendingFormatRef.current = pendingFormatRef.current || {};
        pendingFormatRef.current.color = color;
        setSelectionState(prev => ({ ...prev, foreColor: color }));
        return;
      }
      const span = document.createElement('span');
      span.style.color = color;
      try {
        range.surroundContents(span);
      } catch (e) {
        const fragment = range.extractContents();
        span.appendChild(fragment);
        range.insertNode(span);
      }
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
      pushToHistory();
      updateSelectionState();
    }, [pushToHistory, updateSelectionState]);

    const setBackColor = useCallback((color: string): void => {
      const result = document.execCommand('hiliteColor', false, color);
      if (!result) {
        document.execCommand('backColor', false, color);
      }
      pushToHistory();
      updateSelectionState();
      focusEditor();
    }, [pushToHistory, updateSelectionState, focusEditor]);

    // -----------------------------------------------------------------------
    // Justification & line height
    // -----------------------------------------------------------------------
    const justifyLeft = useCallback(() => execCommand('justifyLeft'), [execCommand]);
    const justifyCenter = useCallback(() => execCommand('justifyCenter'), [execCommand]);
    const justifyRight = useCallback(() => execCommand('justifyRight'), [execCommand]);
    const justifyFull = useCallback(() => execCommand('justifyFull'), [execCommand]);

    const setLineHeight = useCallback((heightVal: string): void => {
      let range: Range | null = null;
      if (savedSelectionRef.current) {
        range = savedSelectionRef.current;
        restoreSelection(savedSelectionRef.current);
      } else {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        range = selection.getRangeAt(0);
      }

      if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) {
        return;
      }

      const blockElements = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE'];

      const findBlockParent = (n: Node | null): HTMLElement | null => {
        while (n && n !== editorContentRef.current) {
          if (n.nodeType === Node.ELEMENT_NODE && blockElements.indexOf((n as Element).tagName) !== -1) {
            return n as HTMLElement;
          }
          n = n.parentNode;
        }
        return null;
      };

      const blocksToUpdate = new Set<HTMLElement>();

      let commonAncestor: Node | null = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.TEXT_NODE) {
        commonAncestor = commonAncestor.parentNode;
      }

      if (range.collapsed) {
        const block = findBlockParent(commonAncestor);
        if (block) {
          blocksToUpdate.add(block);
        } else if (commonAncestor === editorContentRef.current || editorContentRef.current!.contains(commonAncestor)) {
          const editorEl = editorContentRef.current as HTMLElement;
          const childNodes = Array.from(editorEl.childNodes);
          if (childNodes.length > 0) {
            let targetNode: Node = range.startContainer;
            if (targetNode.nodeType === Node.TEXT_NODE) {
              targetNode = targetNode.parentNode as Node;
            }
            if (targetNode === editorEl || (targetNode.parentNode === editorEl && blockElements.indexOf((targetNode as Element).tagName) === -1)) {
              document.execCommand('formatBlock', false, 'p');
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) {
                const newRange = sel.getRangeAt(0);
                const newBlock = findBlockParent(newRange.commonAncestorContainer);
                if (newBlock) {
                  blocksToUpdate.add(newBlock);
                }
              }
            }
          }
        }
      } else {
        const editorEl = editorContentRef.current as HTMLElement;
        if (editorEl) {
          const allBlocks = editorEl.querySelectorAll(blockElements.join(','));
          allBlocks.forEach((block) => {
            if (range!.intersectsNode(block)) {
              blocksToUpdate.add(block as HTMLElement);
            }
          });
          if (blocksToUpdate.size === 0) {
            const block = findBlockParent(commonAncestor);
            if (block) {
              blocksToUpdate.add(block);
            } else {
              document.execCommand('formatBlock', false, 'p');
              const newBlocks = editorEl.querySelectorAll(blockElements.join(','));
              newBlocks.forEach((block) => {
                if (range!.intersectsNode(block)) {
                  blocksToUpdate.add(block as HTMLElement);
                }
              });
            }
          }
        }
      }

      blocksToUpdate.forEach((block) => {
        block.style.lineHeight = heightVal;
      });

      savedSelectionRef.current = null;
      pushToHistory();
      updateSelectionState();
    }, [restoreSelection, pushToHistory, updateSelectionState]);

    // -----------------------------------------------------------------------
    // Clean / copy / apply format
    // -----------------------------------------------------------------------
    const cleanFormatting = useCallback((): void => {
      execCommand('removeFormat');
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editorContentRef.current && editorContentRef.current.contains(range.commonAncestorContainer)) {
          const contents = range.cloneContents();
          const tempDiv = document.createElement('div');
          tempDiv.appendChild(contents);
          const elements = tempDiv.querySelectorAll('*');
          elements.forEach((el: Element) => {
            (el as HTMLElement).removeAttribute('style');
          });
          range.deleteContents();
          const fragment = document.createRange().createContextualFragment(tempDiv.innerHTML);
          range.insertNode(fragment);
          pushToHistory();
        }
      }
    }, [execCommand, pushToHistory]);

    const copyFormat = useCallback((): void => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      let node: Node | null = range.commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode;
      }
      if (node && node.nodeType === Node.ELEMENT_NODE) {
        const computedStyle = window.getComputedStyle(node as Element);
        copiedFormatRef.current = {
          fontFamily: computedStyle.fontFamily,
          fontSize: computedStyle.fontSize,
          fontWeight: computedStyle.fontWeight,
          fontStyle: computedStyle.fontStyle,
          textDecoration: computedStyle.textDecoration,
          color: computedStyle.color,
          backgroundColor: computedStyle.backgroundColor,
        };
      }
    }, []);

    const applyFormat = useCallback((): void => {
      if (!copiedFormatRef.current) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) {
        return;
      }
      const contents = range.extractContents();
      const span = document.createElement('span');
      span.style.fontFamily = copiedFormatRef.current.fontFamily;
      span.style.fontSize = copiedFormatRef.current.fontSize;
      span.style.fontWeight = copiedFormatRef.current.fontWeight;
      span.style.fontStyle = copiedFormatRef.current.fontStyle;
      span.style.textDecoration = copiedFormatRef.current.textDecoration;
      span.style.color = copiedFormatRef.current.color;
      span.style.backgroundColor = copiedFormatRef.current.backgroundColor;
      span.appendChild(contents);
      range.insertNode(span);
      range.setStartAfter(span);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      pushToHistory();
    }, [pushToHistory]);

    // -----------------------------------------------------------------------
    // List operations
    // -----------------------------------------------------------------------
    const insertOrderedList = useCallback((): void => {
      const existingOl = getCurrentListElement();
      if (existingOl && existingOl.tagName === 'OL') {
        const listType = existingOl.getAttribute('type') || (existingOl as HTMLOListElement).style.listStyleType || '';
        if (listType && listType !== '1' && listType !== 'decimal') {
          (existingOl as HTMLOListElement).style.listStyleType = 'decimal';
          existingOl.setAttribute('type', '1');
          pushToHistory();
          updateSelectionState();
          return;
        }
      }
      execCommand('insertOrderedList');
    }, [getCurrentListElement, pushToHistory, updateSelectionState, execCommand]);

    const insertUnorderedList = useCallback((): void => {
      execCommand('insertUnorderedList');
    }, [execCommand]);

    const insertUpperRomanList = useCallback((): void => {
      const existingOl = getCurrentListElement();
      if (existingOl && existingOl.tagName === 'OL') {
        (existingOl as HTMLOListElement).style.listStyleType = 'upper-roman';
        existingOl.setAttribute('type', 'I');
        pushToHistory();
        updateSelectionState();
      } else {
        insertOrderedList();
        const ol = getCurrentListElement();
        if (ol && ol.tagName === 'OL') {
          (ol as HTMLOListElement).style.listStyleType = 'upper-roman';
          ol.setAttribute('type', 'I');
          pushToHistory();
        }
      }
    }, [getCurrentListElement, pushToHistory, updateSelectionState, insertOrderedList]);

    const insertLowerRomanList = useCallback((): void => {
      const existingOl = getCurrentListElement();
      if (existingOl && existingOl.tagName === 'OL') {
        (existingOl as HTMLOListElement).style.listStyleType = 'lower-roman';
        existingOl.setAttribute('type', 'i');
        pushToHistory();
        updateSelectionState();
      } else {
        insertOrderedList();
        const ol = getCurrentListElement();
        if (ol && ol.tagName === 'OL') {
          (ol as HTMLOListElement).style.listStyleType = 'lower-roman';
          ol.setAttribute('type', 'i');
          pushToHistory();
        }
      }
    }, [getCurrentListElement, pushToHistory, updateSelectionState, insertOrderedList]);

    const indent = useCallback((): void => {
      const listElement = getCurrentListElement();
      if (listElement) {
        execCommand('indent');
      } else {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) return;

        let node: Node | null = range.commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;

        const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'];
        let blockParent: HTMLElement | null = null;
        while (node && node !== editorContentRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE && blockTags.indexOf((node as Element).tagName) !== -1) {
            blockParent = node as HTMLElement;
            break;
          }
          node = node.parentNode;
        }

        if (blockParent) {
          const currentMargin = parseInt(blockParent.style.marginLeft || '0', 10);
          blockParent.style.marginLeft = (currentMargin + 40) + 'px';
        } else {
          execCommand('formatBlock', 'blockquote');
        }
        pushToHistory();
        updateSelectionState();
      }
    }, [getCurrentListElement, execCommand, pushToHistory, updateSelectionState]);

    const outdent = useCallback((): void => {
      const listElement = getCurrentListElement();
      if (listElement) {
        execCommand('outdent');
      } else {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!editorContentRef.current || !editorContentRef.current.contains(range.commonAncestorContainer)) return;

        let node: Node | null = range.commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;

        const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'];
        let blockParent: HTMLElement | null = null;
        while (node && node !== editorContentRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE && blockTags.indexOf((node as Element).tagName) !== -1) {
            blockParent = node as HTMLElement;
            break;
          }
          node = node.parentNode;
        }

        if (blockParent) {
          if (blockParent.tagName === 'BLOCKQUOTE') {
            execCommand('outdent');
          } else {
            const currentMargin = parseInt(blockParent.style.marginLeft || '0', 10);
            const newMargin = Math.max(0, currentMargin - 40);
            if (newMargin === 0) {
              blockParent.style.marginLeft = '';
            } else {
              blockParent.style.marginLeft = newMargin + 'px';
            }
          }
        }
        pushToHistory();
        updateSelectionState();
      }
    }, [getCurrentListElement, execCommand, pushToHistory, updateSelectionState]);

    // -----------------------------------------------------------------------
    // Undo / Redo
    // -----------------------------------------------------------------------
    const undo = useCallback((): void => {
      if (historyIndexRef.current <= 0) return;
      historyIndexRef.current--;
      const content = historyRef.current[historyIndexRef.current];
      if (editorContentRef.current) {
        editorContentRef.current.innerHTML = content;
        editorContentStrRef.current = content;
      }
      onChange?.(content);
      forceRender(c => c + 1);
    }, [onChange]);

    const redo = useCallback((): void => {
      if (historyIndexRef.current >= historyRef.current.length - 1) return;
      historyIndexRef.current++;
      const content = historyRef.current[historyIndexRef.current];
      if (editorContentRef.current) {
        editorContentRef.current.innerHTML = content;
        editorContentStrRef.current = content;
      }
      onChange?.(content);
      forceRender(c => c + 1);
    }, [onChange]);

    // -----------------------------------------------------------------------
    // Table helpers
    // -----------------------------------------------------------------------
    const getCurrentTable = useCallback((): HTMLTableElement | null => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;
      let node: Node | null = selection.anchorNode;
      if (!node) return null;
      while (node) {
        if (node === editorContentRef.current) return null;
        if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'TABLE') {
          return node as HTMLTableElement;
        }
        node = node.parentNode;
      }
      return null;
    }, []);

    const getCurrentCell = useCallback((): HTMLTableCellElement | null => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;
      let node: Node | null = selection.anchorNode;
      if (!node) return null;
      while (node) {
        if (node === editorContentRef.current) return null;
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = (node as Element).tagName;
          if (tagName === 'TD' || tagName === 'TH') {
            return node as HTMLTableCellElement;
          }
        }
        node = node.parentNode;
      }
      return null;
    }, []);

    const getCurrentRow = useCallback((): HTMLTableRowElement | null => {
      const cell = getCurrentCell();
      if (!cell) return null;
      return cell.parentElement as HTMLTableRowElement;
    }, [getCurrentCell]);

    const getTableGrid = useCallback((table: HTMLTableElement): { cell: HTMLTableCellElement | null; rowIndex: number; colIndex: number }[][] => {
      const rows = table.rows;
      const grid: { cell: HTMLTableCellElement | null; rowIndex: number; colIndex: number }[][] = [];

      let maxCols = 0;
      for (let r = 0; r < rows.length; r++) {
        let colCount = 0;
        for (let c = 0; c < rows[r].cells.length; c++) {
          colCount += rows[r].cells[c].colSpan || 1;
        }
        maxCols = Math.max(maxCols, colCount);
      }

      for (let r = 0; r < rows.length; r++) {
        grid[r] = [];
        for (let c = 0; c < maxCols; c++) {
          grid[r][c] = { cell: null, rowIndex: r, colIndex: c };
        }
      }

      for (let r = 0; r < rows.length; r++) {
        let colIndex = 0;
        for (let c = 0; c < rows[r].cells.length; c++) {
          const cell = rows[r].cells[c];
          const rowSpan = cell.rowSpan || 1;
          const colSpan = cell.colSpan || 1;
          while (colIndex < maxCols && grid[r][colIndex].cell !== null) {
            colIndex++;
          }
          for (let rs = 0; rs < rowSpan && r + rs < rows.length; rs++) {
            for (let cs = 0; cs < colSpan && colIndex + cs < maxCols; cs++) {
              grid[r + rs][colIndex + cs] = { cell, rowIndex: r, colIndex };
            }
          }
          colIndex += colSpan;
        }
      }

      return grid;
    }, []);

    const createTable = useCallback((rows: number, cols: number): void => {
      const table = document.createElement('table');
      table.style.borderCollapse = 'collapse';
      table.style.width = '100%';
      const tbody = document.createElement('tbody');
      for (let r = 0; r < rows; r++) {
        const tr = document.createElement('tr');
        for (let c = 0; c < cols; c++) {
          const td = document.createElement('td');
          td.style.border = '1px solid #000';
          td.style.padding = '8px';
          td.style.minWidth = '50px';
          td.innerHTML = '<br>';
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      insertHtml(table.outerHTML);
      pushToHistory();
    }, [insertHtml, pushToHistory]);

    const addRowAbove = useCallback((): void => {
      const row = getCurrentRow();
      if (!row) return;
      const table = getCurrentTable();
      if (!table) return;
      const newRow = document.createElement('tr');
      const cells = row.cells;
      for (let i = 0; i < cells.length; i++) {
        const newCell = document.createElement('td');
        newCell.style.cssText = cells[i].style.cssText;
        newCell.innerHTML = '<br>';
        newRow.appendChild(newCell);
      }
      row.parentNode?.insertBefore(newRow, row);
      pushToHistory();
    }, [getCurrentRow, getCurrentTable, pushToHistory]);

    const addRowBelow = useCallback((): void => {
      const row = getCurrentRow();
      if (!row) return;
      const newRow = document.createElement('tr');
      const cells = row.cells;
      for (let i = 0; i < cells.length; i++) {
        const newCell = document.createElement('td');
        newCell.style.cssText = cells[i].style.cssText;
        newCell.innerHTML = '<br>';
        newRow.appendChild(newCell);
      }
      if (row.nextSibling) {
        row.parentNode?.insertBefore(newRow, row.nextSibling);
      } else {
        row.parentNode?.appendChild(newRow);
      }
      pushToHistory();
    }, [getCurrentRow, pushToHistory]);

    const deleteRow = useCallback((): void => {
      const row = getCurrentRow();
      if (!row) return;
      const table = getCurrentTable();
      if (!table) return;
      if (table.rows.length <= 1) {
        table.parentNode?.removeChild(table);
      } else {
        row.parentNode?.removeChild(row);
      }
      pushToHistory();
    }, [getCurrentRow, getCurrentTable, pushToHistory]);

    const addColumnLeft = useCallback((): void => {
      const currentCell = getCurrentCell();
      const table = getCurrentTable();
      if (!currentCell || !table) return;
      const grid = getTableGrid(table);

      let targetColIndex = -1;
      for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[r].length; c++) {
          if (grid[r][c].cell === currentCell) {
            targetColIndex = c;
            break;
          }
        }
        if (targetColIndex >= 0) break;
      }
      if (targetColIndex < 0) return;

      const rows = table.rows;
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        const gridEntry = grid[r][targetColIndex];
        if (gridEntry.cell && gridEntry.colIndex < targetColIndex) {
          gridEntry.cell.colSpan = (gridEntry.cell.colSpan || 1) + 1;
        } else {
          let insertIndex = 0;
          for (let c = 0; c < targetColIndex; c++) {
            const entry = grid[r][c];
            if (entry.cell && entry.colIndex === c && entry.rowIndex === r) {
              insertIndex++;
            }
          }
          const newCell = document.createElement('td');
          newCell.style.border = '1px solid #000';
          newCell.style.padding = '8px';
          newCell.style.minWidth = '50px';
          newCell.innerHTML = '<br>';
          if (insertIndex < row.cells.length) {
            row.insertBefore(newCell, row.cells[insertIndex]);
          } else {
            row.appendChild(newCell);
          }
        }
      }
      pushToHistory();
    }, [getCurrentCell, getCurrentTable, getTableGrid, pushToHistory]);

    const addColumnRight = useCallback((): void => {
      const currentCell = getCurrentCell();
      const table = getCurrentTable();
      if (!currentCell || !table) return;
      const grid = getTableGrid(table);

      let targetColIndex = -1;
      for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[r].length; c++) {
          if (grid[r][c].cell === currentCell) {
            const colSpan = currentCell.colSpan || 1;
            targetColIndex = grid[r][c].colIndex + colSpan - 1;
            break;
          }
        }
        if (targetColIndex >= 0) break;
      }
      if (targetColIndex < 0) return;

      const rows = table.rows;
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        const gridEntry = grid[r][targetColIndex];
        if (gridEntry.cell && gridEntry.colIndex + (gridEntry.cell.colSpan || 1) - 1 > targetColIndex) {
          gridEntry.cell.colSpan = (gridEntry.cell.colSpan || 1) + 1;
        } else {
          let insertIndex = 0;
          for (let c = 0; c <= targetColIndex; c++) {
            const entry = grid[r][c];
            if (entry.cell && entry.colIndex === c && entry.rowIndex === r) {
              insertIndex++;
            }
          }
          const newCell = document.createElement('td');
          newCell.style.border = '1px solid #000';
          newCell.style.padding = '8px';
          newCell.style.minWidth = '50px';
          newCell.innerHTML = '<br>';
          if (insertIndex < row.cells.length) {
            row.insertBefore(newCell, row.cells[insertIndex]);
          } else {
            row.appendChild(newCell);
          }
        }
      }
      pushToHistory();
    }, [getCurrentCell, getCurrentTable, getTableGrid, pushToHistory]);

    const deleteColumn = useCallback((): void => {
      const currentCell = getCurrentCell();
      const table = getCurrentTable();
      if (!currentCell || !table) return;
      const grid = getTableGrid(table);

      let targetColIndex = -1;
      for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[r].length; c++) {
          if (grid[r][c].cell === currentCell) {
            targetColIndex = c;
            break;
          }
        }
        if (targetColIndex >= 0) break;
      }
      if (targetColIndex < 0) return;

      if (grid[0].length <= 1) {
        table.parentNode?.removeChild(table);
        pushToHistory();
        return;
      }

      const processedCells = new Set<HTMLTableCellElement>();
      const rows = table.rows;
      for (let r = 0; r < rows.length; r++) {
        const gridEntry = grid[r][targetColIndex];
        const cell = gridEntry.cell;
        if (!cell || processedCells.has(cell)) continue;
        processedCells.add(cell);
        const colSpan = cell.colSpan || 1;
        if (colSpan > 1) {
          cell.colSpan = colSpan - 1;
        } else {
          cell.parentNode?.removeChild(cell);
        }
      }
      pushToHistory();
    }, [getCurrentCell, getCurrentTable, getTableGrid, pushToHistory]);

    // -----------------------------------------------------------------------
    // Cell merge / split operations
    // -----------------------------------------------------------------------
    const mergeCellsHorizontally = useCallback((): void => {
      const currentCell = getCurrentCell();
      if (!currentCell) return;
      const nextCell = currentCell.nextElementSibling as HTMLTableCellElement | null;
      if (!nextCell || (nextCell.tagName !== 'TD' && nextCell.tagName !== 'TH')) return;

      const nextContent = nextCell.innerHTML;
      if (nextContent && nextContent !== '<br>') {
        if (currentCell.innerHTML === '<br>') {
          currentCell.innerHTML = nextContent;
        } else {
          currentCell.innerHTML += nextContent;
        }
      }
      const currentColspan = currentCell.colSpan || 1;
      const nextColspan = nextCell.colSpan || 1;
      currentCell.colSpan = currentColspan + nextColspan;
      nextCell.parentNode?.removeChild(nextCell);
      pushToHistory();
    }, [getCurrentCell, pushToHistory]);

    const mergeCellsVertically = useCallback((): void => {
      const currentCell = getCurrentCell();
      const currentRow = getCurrentRow();
      const table = getCurrentTable();
      if (!currentCell || !currentRow || !table) return;

      const grid = getTableGrid(table);

      let cellRowIndex = -1;
      let cellColIndex = -1;
      for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[r].length; c++) {
          if (grid[r][c].cell === currentCell && grid[r][c].rowIndex === r) {
            cellRowIndex = r;
            cellColIndex = c;
            break;
          }
        }
        if (cellRowIndex >= 0) break;
      }
      if (cellRowIndex < 0) return;

      const currentRowspan = currentCell.rowSpan || 1;
      const belowRowIndex = cellRowIndex + currentRowspan;
      if (belowRowIndex >= grid.length) return;

      const belowEntry = grid[belowRowIndex][cellColIndex];
      const belowCell = belowEntry.cell;
      if (!belowCell || belowEntry.rowIndex !== belowRowIndex) return;

      const belowContent = belowCell.innerHTML;
      if (belowContent && belowContent !== '<br>') {
        if (currentCell.innerHTML === '<br>') {
          currentCell.innerHTML = belowContent;
        } else {
          currentCell.innerHTML += belowContent;
        }
      }
      const belowRowspan = belowCell.rowSpan || 1;
      currentCell.rowSpan = currentRowspan + belowRowspan;
      belowCell.parentNode?.removeChild(belowCell);
      pushToHistory();
    }, [getCurrentCell, getCurrentRow, getCurrentTable, getTableGrid, pushToHistory]);

    const splitCellHorizontally = useCallback((): void => {
      const currentCell = getCurrentCell();
      if (!currentCell) return;
      const colSpan = currentCell.colSpan || 1;

      if (colSpan > 1) {
        currentCell.colSpan = colSpan - 1;
        const newCell = document.createElement('td');
        newCell.style.cssText = currentCell.style.cssText;
        newCell.innerHTML = '<br>';
        if (currentCell.nextSibling) {
          currentCell.parentNode?.insertBefore(newCell, currentCell.nextSibling);
        } else {
          currentCell.parentNode?.appendChild(newCell);
        }
      } else {
        const table = getCurrentTable();
        if (!table) return;
        const grid = getTableGrid(table);

        let targetColIndex = -1;
        let targetRowIndex = -1;
        for (let r = 0; r < grid.length; r++) {
          for (let c = 0; c < grid[r].length; c++) {
            if (grid[r][c].cell === currentCell) {
              targetColIndex = c;
              targetRowIndex = r;
              break;
            }
          }
          if (targetColIndex >= 0) break;
        }
        if (targetColIndex < 0) return;

        const newCell = document.createElement('td');
        newCell.style.cssText = currentCell.style.cssText;
        newCell.innerHTML = '<br>';
        if (currentCell.nextSibling) {
          currentCell.parentNode?.insertBefore(newCell, currentCell.nextSibling);
        } else {
          currentCell.parentNode?.appendChild(newCell);
        }

        const rows = table.rows;
        for (let r = 0; r < rows.length; r++) {
          if (r === targetRowIndex) continue;
          const gridEntry = grid[r][targetColIndex];
          if (gridEntry.cell) {
            if (gridEntry.colIndex === targetColIndex && gridEntry.rowIndex === r) {
              gridEntry.cell.colSpan = (gridEntry.cell.colSpan || 1) + 1;
            }
          }
        }
      }
      pushToHistory();
    }, [getCurrentCell, getCurrentTable, getTableGrid, pushToHistory]);

    const splitCellVertically = useCallback((): void => {
      const currentCell = getCurrentCell();
      const currentRow = getCurrentRow();
      if (!currentCell || !currentRow) return;
      const rowSpan = currentCell.rowSpan || 1;

      if (rowSpan > 1) {
        currentCell.rowSpan = rowSpan - 1;
        const table = getCurrentTable();
        if (!table) return;
        const grid = getTableGrid(table);

        let cellRowIndex = -1;
        let cellColIndex = -1;
        for (let r = 0; r < grid.length; r++) {
          for (let c = 0; c < grid[r].length; c++) {
            if (grid[r][c].cell === currentCell && grid[r][c].rowIndex === r) {
              cellRowIndex = r;
              cellColIndex = c;
              break;
            }
          }
          if (cellRowIndex >= 0) break;
        }
        if (cellRowIndex < 0) return;

        const targetRowIndex = cellRowIndex + rowSpan - 1;
        if (targetRowIndex >= table.rows.length) return;
        const targetRow = table.rows[targetRowIndex];

        let insertIndex = 0;
        for (let c = 0; c < cellColIndex; c++) {
          const entry = grid[targetRowIndex][c];
          if (entry.cell && entry.rowIndex === targetRowIndex && entry.colIndex === c) {
            insertIndex++;
          }
        }

        const newCell = document.createElement('td');
        newCell.style.cssText = currentCell.style.cssText;
        newCell.colSpan = currentCell.colSpan || 1;
        newCell.innerHTML = '<br>';
        if (insertIndex < targetRow.cells.length) {
          targetRow.insertBefore(newCell, targetRow.cells[insertIndex]);
        } else {
          targetRow.appendChild(newCell);
        }
      } else {
        const table = getCurrentTable();
        if (!table) return;
        const newRow = document.createElement('tr');
        const cells = currentRow.cells;
        for (let i = 0; i < cells.length; i++) {
          const cell = cells[i];
          if (cell === currentCell) {
            const newCell = document.createElement('td');
            newCell.style.cssText = cell.style.cssText;
            newCell.colSpan = cell.colSpan || 1;
            newCell.innerHTML = '<br>';
            newRow.appendChild(newCell);
          } else {
            cell.rowSpan = (cell.rowSpan || 1) + 1;
          }
        }
        if (currentRow.nextSibling) {
          currentRow.parentNode?.insertBefore(newRow, currentRow.nextSibling);
        } else {
          currentRow.parentNode?.appendChild(newRow);
        }
      }
      pushToHistory();
    }, [getCurrentCell, getCurrentRow, getCurrentTable, getTableGrid, pushToHistory]);

    // -----------------------------------------------------------------------
    // Table alignment
    // -----------------------------------------------------------------------
    const tableAlignLeft = useCallback((): void => {
      const table = getCurrentTable();
      if (!table) return;
      table.style.marginLeft = '0';
      table.style.marginRight = 'auto';
      pushToHistory();
    }, [getCurrentTable, pushToHistory]);

    const tableAlignCenter = useCallback((): void => {
      const table = getCurrentTable();
      if (!table) return;
      table.style.marginLeft = 'auto';
      table.style.marginRight = 'auto';
      pushToHistory();
    }, [getCurrentTable, pushToHistory]);

    const tableAlignRight = useCallback((): void => {
      const table = getCurrentTable();
      if (!table) return;
      table.style.marginLeft = 'auto';
      table.style.marginRight = '0';
      pushToHistory();
    }, [getCurrentTable, pushToHistory]);

    // -----------------------------------------------------------------------
    // Print / PDF
    // -----------------------------------------------------------------------
    const printContent = useCallback((): void => {
      const content = editorContentRef.current?.innerHTML || '';
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      document.body.appendChild(printFrame);

      const printDoc = printFrame.contentWindow?.document;
      if (printDoc) {
        printDoc.open();
        printDoc.write(`
          <!DOCTYPE html>
          <html>
          <head>
              <title>Print</title>
              <style>
                  @page { margin: 0; }
                  body {
                      font-family: Arial, sans-serif;
                      font-size: 12pt;
                      line-height: 1.5;
                      margin: 0;
                      padding: 0.75in;
                      color: #000;
                  }
                  img { max-width: 100%; height: auto; }
                  table { border-collapse: collapse; width: 100%; }
                  td, th { border: 1px solid #000; padding: 8px; }
                  a { color: #000; text-decoration: underline; }
              </style>
          </head>
          <body>${content}</body>
          </html>
        `);
        printDoc.close();
        setTimeout(() => {
          printFrame.contentWindow?.print();
          setTimeout(() => {
            if (document.body.contains(printFrame)) {
              document.body.removeChild(printFrame);
            }
          }, 1000);
        }, 300);
      }
    }, []);

    const showExportPdfHelp = useCallback((): void => {
      onExportPdf?.();
    }, [onExportPdf]);

    // -----------------------------------------------------------------------
    // Link dialog
    // -----------------------------------------------------------------------
    const unlink = useCallback((): void => {
      let anchorNode: Node | null = null;
      let wasCollapsed = true;

      if (savedSelectionRef.current) {
        anchorNode = savedSelectionRef.current.startContainer;
        wasCollapsed = savedSelectionRef.current.collapsed;
      } else {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          anchorNode = selection.anchorNode;
          wasCollapsed = selection.isCollapsed;
        }
      }

      const linkElement = findAncestor(anchorNode, 'A') as HTMLAnchorElement | null;

      if (linkElement) {
        const range = document.createRange();
        range.selectNodeContents(linkElement);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        focusEditor();
        document.execCommand('unlink', false, undefined);
        if (wasCollapsed && selection) {
          selection.collapseToEnd();
        }
        pushToHistory();
        updateSelectionState();
      } else {
        execCommand('unlink');
      }
      savedSelectionRef.current = null;
    }, [findAncestor, focusEditor, pushToHistory, updateSelectionState, execCommand]);

    const openLinkDialog = useCallback((): void => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const anchor = selection.anchorNode;
        const linkElement = findAncestor(anchor, 'A') as HTMLAnchorElement | null;
        if (linkElement) {
          setLinkDialogUrl(linkElement.href);
          setLinkDialogText(linkElement.textContent || '');
          setLinkDialogTarget(linkElement.target || '_self');
          setLinkDialogIsEdit(true);
        } else {
          setLinkDialogUrl('');
          setLinkDialogText(getSelectedHtml().replace(/<[^>]*>/g, ''));
          setLinkDialogTarget('_self');
          setLinkDialogIsEdit(false);
        }
      }
      savedSelectionRef.current = saveSelection();
      setLinkDialogOpen(true);
    }, [findAncestor, getSelectedHtml, saveSelection]);

    const handleLinkInsert = useCallback((data: LinkDialogInsertData): void => {
      if (savedSelectionRef.current) {
        restoreSelection(savedSelectionRef.current);
      }

      const link = document.createElement('a');
      link.href = data.url;
      link.textContent = data.text;
      if (data.target !== '_self') {
        link.target = data.target;
      }

      if (linkDialogIsEdit) {
        const selection = window.getSelection();
        if (selection) {
          const anchor = selection.anchorNode;
          const existingLink = findAncestor(anchor, 'A');
          if (existingLink) {
            existingLink.parentNode?.replaceChild(link, existingLink);
          }
        }
      } else {
        insertHtml(link.outerHTML);
      }

      setLinkDialogOpen(false);
      pushToHistory();
    }, [restoreSelection, findAncestor, insertHtml, pushToHistory, linkDialogIsEdit]);

    // -----------------------------------------------------------------------
    // Image dialog
    // -----------------------------------------------------------------------
    const openImageDialog = useCallback((): void => {
      setImageDialogSrc('');
      setImageDialogAlt('');
      setImageDialogWidth('');
      setImageDialogHeight('');
      savedSelectionRef.current = saveSelection();
      setImageDialogOpen(true);
    }, [saveSelection]);

    const handleImageInsert = useCallback((data: ImageDialogInsertData): void => {
      if (savedSelectionRef.current) {
        restoreSelection(savedSelectionRef.current);
      }
      let imgHtml = '<img src="' + data.src + '"';
      if (data.alt) imgHtml += ' alt="' + data.alt + '"';
      if (data.width) imgHtml += ' width="' + data.width + '"';
      if (data.height) imgHtml += ' height="' + data.height + '"';
      imgHtml += '>';
      insertHtml(imgHtml);
      setImageDialogOpen(false);
      pushToHistory();
    }, [restoreSelection, insertHtml, pushToHistory]);

    const handleImageUploadInternal = useCallback((): void => {
      if (!onImageUpload) {
        console.warn('No onImageUpload handler provided');
        return;
      }
      savedSelectionRef.current = saveSelection();
      onImageUpload((imageUrl: string, alt?: string, width?: string, height?: string, attachmentId?: number | string) => {
        if (savedSelectionRef.current) {
          restoreSelection(savedSelectionRef.current);
        }
        if (imageUrl) {
          let imgHtml = '<img src="' + imageUrl + '"';
          if (attachmentId) imgHtml += ' data-attachment-id="' + attachmentId + '"';
          if (alt) imgHtml += ' alt="' + alt + '"';
          if (width) imgHtml += ' width="' + width + '"';
          if (height) imgHtml += ' height="' + height + '"';
          imgHtml += '>';
          insertHtml(imgHtml);
          pushToHistory();
        }
        savedSelectionRef.current = null;
      });
    }, [onImageUpload, saveSelection, restoreSelection, insertHtml, pushToHistory]);

    // -----------------------------------------------------------------------
    // File dialog
    // -----------------------------------------------------------------------
    const openFileDialog = useCallback((): void => {
      setFileDialogUrl('');
      setFileDialogText('');
      savedSelectionRef.current = saveSelection();
      setFileDialogOpen(true);
    }, [saveSelection]);

    const handleFileInsert = useCallback((data: FileDialogInsertData): void => {
      if (savedSelectionRef.current) {
        restoreSelection(savedSelectionRef.current);
      }
      const link = '<a href="' + data.url + '" target="_blank">' + data.text + '</a>';
      insertHtml(link);
      setFileDialogOpen(false);
      pushToHistory();
    }, [restoreSelection, insertHtml, pushToHistory]);

    // -----------------------------------------------------------------------
    // Table wizard
    // -----------------------------------------------------------------------
    const openTableWizard = useCallback((): void => {
      savedSelectionRef.current = saveSelection();
      setTableWizardOpen(true);
    }, [saveSelection]);

    const handleTableCreate = useCallback((data: { rows: number; cols: number }): void => {
      if (savedSelectionRef.current) {
        restoreSelection(savedSelectionRef.current);
      }
      createTable(data.rows, data.cols);
      setTableWizardOpen(false);
    }, [restoreSelection, createTable]);

    // -----------------------------------------------------------------------
    // Color picker
    // -----------------------------------------------------------------------
    const openColorPicker = useCallback((type: 'fore' | 'back'): void => {
      setColorPickerType(type);
      setColorPickerCurrentColor(
        type === 'fore'
          ? (selectionState.foreColor || '#000000')
          : (selectionState.backColor || '#ffffff')
      );
      savedSelectionRef.current = saveSelection();
      setColorPickerOpen(true);
    }, [saveSelection, selectionState.foreColor, selectionState.backColor]);

    const handleColorSelect = useCallback((color: string): void => {
      if (savedSelectionRef.current) {
        restoreSelection(savedSelectionRef.current);
      }
      if (colorPickerType === 'fore') {
        setForeColor(color);
      } else {
        setBackColor(color);
      }
      setColorPickerOpen(false);
    }, [restoreSelection, colorPickerType, setForeColor, setBackColor]);

    // -----------------------------------------------------------------------
    // Toolbar command handler
    // -----------------------------------------------------------------------
    const handleToolbarCommand = useCallback((payload: { command: string; value?: any }): void => {
      const { command, value } = payload;
      const commandMap: Record<string, () => void> = {
        bold: toggleBold,
        italic: toggleItalic,
        underline: toggleUnderline,
        strikethrough: toggleStrikethrough,
        setFormatting: () => setFormatting(value),
        setFontName: () => setFontName(value),
        setFontSize: () => setFontSize(value),
        setLineHeight: () => setLineHeight(value),
        justifyLeft,
        justifyCenter,
        justifyRight,
        justifyFull,
        insertOrderedList,
        insertUnorderedList,
        insertUpperRomanList: insertUpperRomanList,
        insertLowerRomanList: insertLowerRomanList,
        indent,
        outdent,
        createLink: openLinkDialog,
        unlink,
        insertImage: openImageDialog,
        uploadImage: handleImageUploadInternal,
        insertFile: openFileDialog,
        openForeColorPicker: () => openColorPicker('fore'),
        openBackColorPicker: () => openColorPicker('back'),
        undo,
        redo,
        cleanFormatting,
        copyFormat,
        applyFormat,
        createTable: openTableWizard,
        addRowAbove,
        addRowBelow,
        addColumnLeft,
        addColumnRight,
        deleteRow,
        deleteColumn,
        mergeCellsHorizontally,
        mergeCellsVertically,
        splitCellHorizontally,
        splitCellVertically,
        tableAlignLeft,
        tableAlignCenter,
        tableAlignRight,
        print: printContent,
        exportPdf: showExportPdfHelp,
      };

      if (commandMap[command]) {
        commandMap[command]();
      }
    }, [
      toggleBold, toggleItalic, toggleUnderline, toggleStrikethrough,
      setFormatting, setFontName, setFontSize, setLineHeight,
      justifyLeft, justifyCenter, justifyRight, justifyFull,
      insertOrderedList, insertUnorderedList, insertUpperRomanList, insertLowerRomanList,
      indent, outdent, openLinkDialog, unlink, openImageDialog,
      handleImageUploadInternal, openFileDialog, openColorPicker,
      undo, redo, cleanFormatting, copyFormat, applyFormat,
      openTableWizard, addRowAbove, addRowBelow, addColumnLeft, addColumnRight,
      deleteRow, deleteColumn, mergeCellsHorizontally, mergeCellsVertically,
      splitCellHorizontally, splitCellVertically,
      tableAlignLeft, tableAlignCenter, tableAlignRight,
      printContent, showExportPdfHelp,
    ]);

    const handleSaveSelection = useCallback((): void => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editorContentRef.current && editorContentRef.current.contains(range.commonAncestorContainer)) {
          savedSelectionRef.current = range.cloneRange();
        }
      }
    }, []);

    // -----------------------------------------------------------------------
    // Event handlers
    // -----------------------------------------------------------------------
    const handleInput = useCallback((event: React.FormEvent<HTMLDivElement>): void => {
      const target = event.currentTarget as HTMLElement;
      editorContentStrRef.current = target.innerHTML;
      updateSelectionState();

      if (historyDebounceTimerRef.current) {
        clearTimeout(historyDebounceTimerRef.current);
      }
      historyDebounceTimerRef.current = window.setTimeout(() => {
        pushToHistory();
        historyDebounceTimerRef.current = null;
      }, 300);
    }, [updateSelectionState, pushToHistory]);

    const handleFocus = useCallback((event: React.FocusEvent): void => {
      setIsFocused(true);
      updateSelectionState();
      onFocus?.(event);
    }, [updateSelectionState, onFocus]);

    const handleBlur = useCallback((event: React.FocusEvent): void => {
      setIsFocused(false);
      const currentSel = saveSelection();
      if (currentSel) {
        savedSelectionRef.current = currentSel;
      }
      onBlur?.(event);
      onChange?.(getContent());
    }, [saveSelection, onBlur, onChange, getContent]);

    const handlePaste = useCallback((event: React.ClipboardEvent): void => {
      if (!sanitizeOnPaste) return;
      event.preventDefault();
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      let original = clipboardData.getData('text/html');
      if (!original) {
        original = clipboardData.getData('text/plain');
        original = original
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
      }
      const sanitized = sanitizeHtml(original);
      insertHtml(sanitized);
      onPaste?.({ original, sanitized });
    }, [sanitizeOnPaste, insertHtml, onPaste]);

    const handleKeydown = useCallback((event: React.KeyboardEvent): void => {
      const isCtrl = event.ctrlKey || event.metaKey;

      if (event.key === 'Tab') {
        if (selectionState.orderedList || selectionState.unorderedList) {
          event.preventDefault();
          if (event.shiftKey) {
            outdent();
          } else {
            indent();
          }
          return;
        }
      }

      if (pendingFormatRef.current && !isCtrl && !event.altKey && event.key.length === 1) {
        applyPendingFormat();
      }

      if (!isCtrl) return;

      switch (event.key.toLowerCase()) {
        case 'z':
          if (event.shiftKey) {
            event.preventDefault();
            redo();
          } else {
            event.preventDefault();
            undo();
          }
          break;
        case 'y':
          event.preventDefault();
          redo();
          break;
        case 'b':
          event.preventDefault();
          toggleBold();
          break;
        case 'i':
          event.preventDefault();
          toggleItalic();
          break;
        case 'u':
          event.preventDefault();
          toggleUnderline();
          break;
      }
    }, [
      selectionState.orderedList, selectionState.unorderedList,
      outdent, indent, applyPendingFormat, redo, undo,
      toggleBold, toggleItalic, toggleUnderline,
    ]);

    // -----------------------------------------------------------------------
    // Lifecycle effects
    // -----------------------------------------------------------------------

    // Check mobile + resize listener (debounced)
    useEffect(() => {
      setIsMobile(window.innerWidth <= 1024); // immediate initial check
      let timer: ReturnType<typeof setTimeout> | null = null;
      const checkIfMobile = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
      };
      window.addEventListener('resize', checkIfMobile);
      return () => {
        if (timer) clearTimeout(timer);
        window.removeEventListener('resize', checkIfMobile);
      };
    }, []);

    // Selection change listener
    useEffect(() => {
      const handleSelectionChange = () => {
        if (selectionDebounceTimerRef.current) {
          clearTimeout(selectionDebounceTimerRef.current);
        }
        selectionDebounceTimerRef.current = window.setTimeout(() => {
          updateSelectionState();
          selectionDebounceTimerRef.current = null;
        }, 50);
      };

      document.addEventListener('selectionchange', handleSelectionChange);
      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
        if (selectionDebounceTimerRef.current) {
          clearTimeout(selectionDebounceTimerRef.current);
        }
      };
    }, [updateSelectionState]);

    // Initialize history with initial value on mount
    useEffect(() => {
      if (mountedRef.current) return;
      mountedRef.current = true;

      if (value) {
        const cleanedValue = stripLegacyFonts(value);
        historyRef.current = [cleanedValue];
        historyIndexRef.current = 0;
        editorContentStrRef.current = cleanedValue;
        // Use requestAnimationFrame to ensure ref is available
        requestAnimationFrame(() => {
          if (editorContentRef.current) {
            editorContentRef.current.innerHTML = cleanedValue;
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Watch value prop changes (equivalent to Vue watch on value)
    useEffect(() => {
      // Skip on mount (handled by the initialization effect above)
      if (!mountedRef.current) return;
      if (value === prevValueRef.current) return;
      prevValueRef.current = value;

      if (value !== editorContentStrRef.current) {
        const cleanedValue = stripLegacyFonts(value || '');
        editorContentStrRef.current = cleanedValue;
        requestAnimationFrame(() => {
          if (editorContentRef.current) {
            editorContentRef.current.innerHTML = cleanedValue;
          }
        });
      }
    }, [value, stripLegacyFonts]);

    // Cleanup debounce timers on unmount
    useEffect(() => {
      return () => {
        if (historyDebounceTimerRef.current) {
          clearTimeout(historyDebounceTimerRef.current);
        }
        if (selectionDebounceTimerRef.current) {
          clearTimeout(selectionDebounceTimerRef.current);
        }
      };
    }, []);

    // -----------------------------------------------------------------------
    // Imperative handle
    // -----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      getContent,
      setContent: setContentInternal,
      insertHtml,
      focus: focusEditor,
      blur: blurEditor,
      printContent,
    }), [getContent, setContentInternal, insertHtml, focusEditor, blurEditor, printContent]);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    const editorStyle: React.CSSProperties = {
      height: typeof height === 'number' ? `${height}px` : height,
      minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
      ...(maxHeight != null ? { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight } : {}),
    };

    return (
      <div className={editorClasses}>
        {showToolbar && (
          <EditorToolbar
            config={resolvedToolbarConfig}
            selectionState={selectionState}
            canUndo={canUndo}
            canRedo={canRedo}
            isReadOnly={isReadOnly}
            isMobile={isMobile}
            theme={activeTheme}
            onCommand={handleToolbarCommand}
            onSaveSelection={handleSaveSelection}
          />
        )}

        <div className="editor-container">
          <div
            ref={editorContentRef}
            className="editor-content"
            contentEditable={!isReadOnly}
            suppressContentEditableWarning
            style={editorStyle}
            onInput={handleInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
            onKeyDown={handleKeydown}
          />
          {isEmpty && !isFocused && placeholder && (
            <div className="editor-placeholder">{placeholder}</div>
          )}
        </div>

        <LinkDialog
          open={linkDialogOpen}
          initialUrl={linkDialogUrl}
          initialText={linkDialogText}
          initialTarget={linkDialogTarget}
          isEdit={linkDialogIsEdit}
          theme={activeTheme}
          onInsert={handleLinkInsert}
          onCancel={() => setLinkDialogOpen(false)}
        />

        <ImageDialog
          open={imageDialogOpen}
          initialSrc={imageDialogSrc}
          initialAlt={imageDialogAlt}
          initialWidth={imageDialogWidth}
          initialHeight={imageDialogHeight}
          theme={activeTheme}
          onInsert={handleImageInsert}
          onCancel={() => setImageDialogOpen(false)}
        />

        <FileDialog
          open={fileDialogOpen}
          initialUrl={fileDialogUrl}
          initialText={fileDialogText}
          theme={activeTheme}
          onInsert={handleFileInsert}
          onCancel={() => setFileDialogOpen(false)}
        />

        <TableWizard
          open={tableWizardOpen}
          theme={activeTheme}
          onCreate={handleTableCreate}
          onCancel={() => setTableWizardOpen(false)}
        />

        <ColorPicker
          open={colorPickerOpen}
          type={colorPickerType}
          currentColor={colorPickerCurrentColor}
          theme={activeTheme}
          onSelect={handleColorSelect}
          onClose={() => setColorPickerOpen(false)}
        />
      </div>
    );
  }
);

(window as any).RichTextEditorReact = RichTextEditor;
